import React from 'react';
import { func, bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { propTypes } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import { toggleLanguageModal } from '../../ducks/UI.duck';
import { subscribeToList } from '../../ducks/newsletterSignup.duck';
import { requestReopenConsent } from '../../ducks/cookieConsent.duck';
import { NamedLink, InlineTextButton, LanguagePill } from '../../components';
import { NewsletterForm } from '../../forms';

import CanadaIcon from './icons/CanadaIcon';

import css from './Footer.module.css';

// The link columns. Cookie settings isn't here: the board shows it twice, and it is kept once, in
// the bottom row, which every mode shows.
const LINK_COLUMNS = [
  {
    titleMsgId: 'Footer.lineColumnTitleFirst',
    links: [
      {
        name: 'ArticlePage',
        params: { topicId: 'live-room', articleId: 'booking-live-room-episodes' },
        labelMsgId: 'Footer.linkToLiveRoom',
      },
      {
        name: 'TopicPage',
        params: { topicId: 'becoming-a-creator' },
        labelMsgId: 'Footer.linkToBecomingACreator',
      },
      {
        name: 'ArticlePage',
        params: { topicId: 'grow-your-community', articleId: 'how-to-grow-your-members' },
        labelMsgId: 'Footer.linkToGrowYourCommunity',
      },
      {
        name: 'ArticlePage',
        params: { topicId: 'profile-and-account-settings', articleId: 'user-badge' },
        labelMsgId: 'Footer.linkToUserBadge',
      },
    ],
  },
  {
    titleMsgId: 'Footer.linkColumnTitleSecond',
    links: [
      { name: 'HelpPage', labelMsgId: 'Footer.linkToHelpPage' },
      { name: 'CommunityPage', labelMsgId: 'Footer.linkToCommunityPage' },
      { name: 'CommunityGuidelinesPage', labelMsgId: 'Footer.linkToCommunityGuidelinesPage' },
    ],
  },
  {
    titleMsgId: 'Footer.linkColumnTitleThird',
    links: [
      { name: 'AboutPage', labelMsgId: 'Footer.linkToAboutPage' },
      { name: 'PrivacyPolicyPage', labelMsgId: 'Footer.linkToPrivacyPolicyPage' },
      { name: 'TermsOfServicePage', labelMsgId: 'Footer.linkToTermsOfServicePage' },
    ],
  },
];

const Newsletter = props => {
  const { currentUser, onSubscribeToList, inProgress, error, submitted } = props;

  const handleSubmit = ({ email }, form) => {
    const profile = currentUser?.attributes?.profile || {};
    const subscribingUser = {
      email,
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
    };
    // The Brevo list is resolved server-side from the 'newsletter' key, so no list id is sent
    // from the browser. The duck keeps the error, and the footer shows it.
    return onSubscribeToList({ subscribingUser })
      .catch(() => null)
      .then(() => form.restart());
  };

  return (
    <div className={css.newsletter}>
      <h2 className={css.newsletterTitle}>
        <FormattedMessage id="Footer.newsletterTitle" />
      </h2>
      {submitted ? (
        <p className={css.newsletterSuccess} role="status">
          <FormattedMessage id="Footer.newsletterSuccessMessage" />
        </p>
      ) : (
        <NewsletterForm onSubmit={handleSubmit} inProgress={inProgress} />
      )}
      {error ? (
        <p className={css.newsletterError} role="alert">
          <FormattedMessage id="Footer.newsletterErrorMessage" />
        </p>
      ) : null}
    </div>
  );
};

const LinkColumns = () => (
  <div className={css.columns}>
    {LINK_COLUMNS.map(column => (
      <div key={column.titleMsgId}>
        <h3 className={css.columnTitle}>
          <FormattedMessage id={column.titleMsgId} />
        </h3>
        <ul className={css.columnList}>
          {column.links.map(link => (
            <li key={link.labelMsgId}>
              <NamedLink className={css.link} name={link.name} params={link.params}>
                <FormattedMessage id={link.labelMsgId} />
              </NamedLink>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const TrustedBy = () => (
  <div className={css.trustedBy}>
    <h3 className={css.trustedByTitle}>
      <FormattedMessage id="Footer.trustedByTitle" />
    </h3>
    <div className={css.trustedByRow}>
      <p className={css.trustedByText}>
        <FormattedMessage id="Footer.trustedByText" />
      </p>
      <span className={css.trustedByDivider} aria-hidden="true" />
      <CanadaIcon className={css.trustedByLogo} aria-hidden="true" focusable="false" />
    </div>
  </div>
);

// Shown in every mode: beta mode (what production runs) renders only this row, and withdrawing
// cookie consent has to be reachable on every page.
const BottomRow = ({ onOpenCookieSettings, onOpenLanguageModal }) => (
  <div className={css.bottom}>
    <span className={css.copyright}>
      <FormattedMessage id="Footer.copyright" />
    </span>
    <div className={css.bottomActions}>
      <InlineTextButton
        rootClassName={css.cookieSettings}
        type="button"
        onClick={onOpenCookieSettings}
      >
        <FormattedMessage id="Footer.cookieSettings" />
      </InlineTextButton>
      <LanguagePill rootClassName={css.language} onClick={onOpenLanguageModal} />
    </div>
  </div>
);

/**
 * The public footer (Landing board): "Stay in the loop" with the Brevo newsletter field, the
 * Features / Community / About link columns, "Trusted by" with the funding line, and a bottom row
 * with the copyright, Cookie settings and the language. On phones it is the compact version: two
 * link columns side by side and the funding line without its title and logo. In beta mode only the
 * bottom row shows.
 */
export const FooterComponent = props => {
  const {
    rootClassName,
    className,
    isBetaMode,
    currentUser,
    onSubscribeToList,
    subscribeToListInProgress,
    subscribeToListError,
    subscribeToListSubmitted,
    onToggleLanguageModal,
    onOpenCookieSettings,
  } = props;

  const betaMode =
    typeof isBetaMode === 'boolean' ? isBetaMode : process.env.REACT_APP_BETA_MODE === 'true';
  const bottomRow = (
    <BottomRow
      onOpenCookieSettings={onOpenCookieSettings}
      onOpenLanguageModal={() => onToggleLanguageModal(true)}
    />
  );

  return (
    <footer className={classNames(rootClassName || css.root, className)}>
      <div className={css.content}>
        {betaMode ? null : (
          <>
            <div className={css.main}>
              <Newsletter
                currentUser={currentUser}
                onSubscribeToList={onSubscribeToList}
                inProgress={subscribeToListInProgress}
                error={subscribeToListError}
                submitted={subscribeToListSubmitted}
              />
              <LinkColumns />
            </div>
            <TrustedBy />
          </>
        )}
        {bottomRow}
      </div>
    </footer>
  );
};

FooterComponent.defaultProps = {
  rootClassName: null,
  className: null,
  isBetaMode: null,
  currentUser: null,
  subscribeToListInProgress: false,
  subscribeToListError: null,
  subscribeToListSubmitted: false,
};

FooterComponent.propTypes = {
  rootClassName: string,
  className: string,
  // Defaults to REACT_APP_BETA_MODE; the Styleguide and tests set it
  isBetaMode: bool,
  currentUser: propTypes.currentUser,

  // newsletter sign-up
  onSubscribeToList: func.isRequired,
  subscribeToListInProgress: bool,
  subscribeToListError: propTypes.error,
  subscribeToListSubmitted: bool,

  onToggleLanguageModal: func.isRequired,
  onOpenCookieSettings: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    subscribeToListInProgress,
    subscribeToListError,
    subscribeToListSubmitted,
  } = state.newsletterSignup;
  return {
    currentUser,
    subscribeToListInProgress,
    subscribeToListError,
    subscribeToListSubmitted,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubscribeToList: bodyParams => dispatch(subscribeToList(bodyParams)),
  onToggleLanguageModal: languageModalOpen => dispatch(toggleLanguageModal(languageModalOpen)),
  onOpenCookieSettings: () => dispatch(requestReopenConsent()),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(FooterComponent);
